import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ScrollView, ActivityIndicator } from 'react-native';
import { MainLayout } from '@app/components';
import {
  catalogSnapshotApi,
  catalogSnapshotToShopApi,
  productApi,
  packApi,
  tagApi,
} from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import { ICatalogSnapshot, ICatalogSnapshotToShop, IProduct, IPack, ITag } from '@app/common';
import { extractData } from '@app/helpers/utils';
import { theme } from '@app/theme';
import {
  CatalogShopItem,
  CatalogShopProductEdit,
  CatalogShopPackEdit,
} from '@app/features/CatalogShop';
import { CatalogHeader } from '@app/features/Catalog';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';

export const CatalogShopScreen = () => {
  const selectedShop = useAppSelector((state) => state.app.selectedShop);

  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [selectedTagId, setSelectedTagId] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState<IProduct | null>(null);
  const [editingPack, setEditingPack] = useState<IPack | null>(null);

  // Get catalog snapshots
  const { data: catalogSnapshotsData, isLoading: isLoadingSnapshots } =
    catalogSnapshotApi.useReadManyCatalogSnapshotsQuery({
      sort: ['publishedAt:DESC'],
      populate: ['connector'],
    });

  const catalogSnapshots = useMemo(() => {
    const data = extractData(catalogSnapshotsData);
    return data.filter((snapshot: ICatalogSnapshot) => snapshot.connector);
  }, [catalogSnapshotsData]);

  // Get catalog-snapshot-to-shops for the selected shop
  const { data: catalogSnapshotToShopsData, isLoading: isLoadingShopData } =
    catalogSnapshotToShopApi.useReadManyCatalogSnapshotToShopsQuery(
      {
        filters: {
          shopId: { $eq: selectedShop?._id },
        },
        populate: ['data'],
      },
      { skip: !selectedShop?._id },
    );

  const catalogSnapshotToShops = useMemo(
    () => extractData(catalogSnapshotToShopsData),
    [catalogSnapshotToShopsData],
  );

  const currentCatalogSnapshotToShop = useMemo(() => {
    return catalogSnapshotToShops?.find(
      (csts: ICatalogSnapshotToShop) => csts.catalogSnapshotId === selectedSnapshotId,
    );
  }, [catalogSnapshotToShops, selectedSnapshotId]);

  // Ref that always holds the latest catalogSnapshotToShop data, updated synchronously
  // This prevents race conditions when multiple toggles happen before React re-renders
  const catalogSnapshotToShopRef = useRef(currentCatalogSnapshotToShop);
  useEffect(() => {
    catalogSnapshotToShopRef.current = currentCatalogSnapshotToShop;
  }, [currentCatalogSnapshotToShop]);

  const getLatestCatalogSnapshotToShop = useCallback(() => {
    return catalogSnapshotToShopRef.current;
  }, []);

  const updateCatalogSnapshotToShopRef = useCallback((newData: ICatalogSnapshotToShop['data']) => {
    if (catalogSnapshotToShopRef.current) {
      catalogSnapshotToShopRef.current = { ...catalogSnapshotToShopRef.current, data: newData };
    }
  }, []);

  // Get all products
  const { data: productsData, isLoading: isLoadingProducts } = productApi.useReadManyProductsQuery({
    sort: ['name:ASC'],
    populate: ['productVariations', 'productVariations.variation', 'tags'],
  });

  const allProducts = useMemo(() => extractData(productsData), [productsData]);

  // Get all packs
  const { data: packsData, isLoading: isLoadingPacks } = packApi.useReadManyPacksQuery({
    sort: ['name:ASC'],
    populate: ['tags'],
  });

  const isLoading = isLoadingSnapshots || isLoadingShopData || isLoadingProducts || isLoadingPacks;

  const allPacks = useMemo(() => extractData(packsData), [packsData]);

  // Get all tags
  const { data: tagsData } = tagApi.useReadManyTagQuery({
    sort: ['name:ASC'],
  });

  const allTags = useMemo(() => extractData(tagsData), [tagsData]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return allProducts.filter((product: IProduct) => {
      const hasSelectedTag = !selectedTagId || product.tagsIds?.includes(selectedTagId);
      const matchesSearch =
        !searchTerm || product.name.toLowerCase().includes(searchTerm.toLowerCase());
      return hasSelectedTag && matchesSearch;
    });
  }, [allProducts, selectedTagId, searchTerm]);

  // Filter packs
  const filteredPacks = useMemo(() => {
    return allPacks.filter((pack: IPack) => {
      const hasSelectedTag = !selectedTagId || pack.tagsIds?.includes(selectedTagId);
      const matchesSearch =
        !searchTerm || pack.name.toLowerCase().includes(searchTerm.toLowerCase());
      return hasSelectedTag && matchesSearch;
    });
  }, [allPacks, selectedTagId, searchTerm]);

  const onSelectTag = useCallback((tag?: ITag) => {
    setSelectedTagId(tag?._id);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  if (!selectedShop) {
    return (
      <MainLayout header={<CatalogHeader />}>
        <EmptyContainer>
          <EmptyText>
            Veuillez sélectionner un restaurant pour gérer les prix personnalisés
          </EmptyText>
        </EmptyContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout header={<CatalogHeader />}>
      <CatalogShopProductEdit
        isOpen={!!editingProduct}
        product={editingProduct}
        catalogSnapshotToShop={currentCatalogSnapshotToShop}
        onClose={() => setEditingProduct(null)}
      />

      <CatalogShopPackEdit
        isOpen={!!editingPack}
        pack={editingPack}
        catalogSnapshotToShop={currentCatalogSnapshotToShop}
        onClose={() => setEditingPack(null)}
      />

      <ScreenContainer>
        {/* Header avec sélecteur de catalogue */}
        <HeaderSection>
          <HeaderTitle>Prix du restaurant</HeaderTitle>
          <HeaderSubtitle>
            Gérez les prix et disponibilités pour {selectedShop.publicName}
          </HeaderSubtitle>
        </HeaderSection>

        {/* Sélecteur de catalogue */}
        <CatalogSelector>
          <SelectorLabel>Catalogue publié</SelectorLabel>
          <CatalogButtonsRow>
            {catalogSnapshots.map((snapshot: ICatalogSnapshot) => (
              <CatalogButton
                key={snapshot._id}
                selected={selectedSnapshotId === snapshot._id}
                onPress={() => setSelectedSnapshotId(snapshot._id)}>
                <CatalogButtonText selected={selectedSnapshotId === snapshot._id}>
                  {snapshot.name}
                </CatalogButtonText>
                <CatalogConnectorText selected={selectedSnapshotId === snapshot._id}>
                  {snapshot.connector?.name}
                </CatalogConnectorText>
              </CatalogButton>
            ))}
          </CatalogButtonsRow>
        </CatalogSelector>

        {isLoading ? (
          <LoadingContainer>
            <ActivityIndicator size="large" color={theme.colors.celadonblue} />
            <LoadingText>Chargement des données...</LoadingText>
          </LoadingContainer>
        ) : selectedSnapshotId ? (
          <ContentContainer>
            {/* Barre de recherche et filtres */}
            <FiltersRow>
              <SearchContainer>
                <SearchInput
                  placeholder="Rechercher un produit ou menu..."
                  value={searchTerm}
                  onChangeText={setSearchTerm}
                  placeholderTextColor="rgba(255,255,255,0.5)"
                />
                {searchTerm.length > 0 && (
                  <ClearButton onPress={clearSearch}>
                    <ClearIcon>×</ClearIcon>
                  </ClearButton>
                )}
              </SearchContainer>

              <TagFiltersContainer horizontal showsHorizontalScrollIndicator={false}>
                <TagButton selected={!selectedTagId} onPress={() => onSelectTag()}>
                  <TagButtonText selected={!selectedTagId}>Toutes</TagButtonText>
                </TagButton>
                {allTags.map((tag: ITag) => (
                  <TagButton
                    key={tag._id}
                    selected={selectedTagId === tag._id}
                    onPress={() => onSelectTag(tag)}>
                    <TagButtonText selected={selectedTagId === tag._id}>{tag.name}</TagButtonText>
                  </TagButton>
                ))}
              </TagFiltersContainer>
            </FiltersRow>

            {/* Liste */}
            <ListContainer>
              {/* En-tête de colonnes */}
              <ListHeader>
                <HeaderCol style={{ width: normalize(60), color: theme.colors.babypowder }}>
                  Actif
                </HeaderCol>
                <HeaderCol style={{ flex: 1, color: theme.colors.babypowder }}>Nom</HeaderCol>
                <HeaderCol style={{ width: normalize(140), color: theme.colors.babypowder }}>
                  Catégorie
                </HeaderCol>
                <HeaderCol
                  style={{
                    width: normalize(200),
                    textAlign: 'right',
                    color: theme.colors.babypowder,
                  }}>
                  Prix
                </HeaderCol>
                <HeaderCol style={{ width: normalize(44), color: theme.colors.babypowder }} />
              </ListHeader>
              <ScrollView showsVerticalScrollIndicator={false}>
                {/* Section Menus */}
                <SectionHeader>
                  <SectionTitle>MENUS</SectionTitle>
                  <SectionCount>{filteredPacks.length}</SectionCount>
                </SectionHeader>

                {filteredPacks.length > 0 ? (
                  filteredPacks.map((pack: IPack) => (
                    <CatalogShopItem
                      key={pack._id}
                      item={pack}
                      type="pack"
                      catalogSnapshotToShop={currentCatalogSnapshotToShop}
                      getLatestCatalogSnapshotToShop={getLatestCatalogSnapshotToShop}
                      updateCatalogSnapshotToShopRef={updateCatalogSnapshotToShopRef}
                      onOpenAdvanced={() => setEditingPack(pack)}
                    />
                  ))
                ) : (
                  <EmptySection>Aucun menu trouvé</EmptySection>
                )}

                {/* Section Produits */}
                <SectionHeader>
                  <SectionTitle>PRODUITS</SectionTitle>
                  <SectionCount>{filteredProducts.length}</SectionCount>
                </SectionHeader>

                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product: IProduct) => (
                    <CatalogShopItem
                      key={product._id}
                      item={product}
                      type="product"
                      catalogSnapshotToShop={currentCatalogSnapshotToShop}
                      getLatestCatalogSnapshotToShop={getLatestCatalogSnapshotToShop}
                      updateCatalogSnapshotToShopRef={updateCatalogSnapshotToShopRef}
                      onOpenAdvanced={() => setEditingProduct(product)}
                    />
                  ))
                ) : (
                  <EmptySection>Aucun produit trouvé</EmptySection>
                )}
              </ScrollView>
            </ListContainer>
          </ContentContainer>
        ) : (
          <SelectCatalogMessage>
            <SelectCatalogText>
              Sélectionnez un catalogue publié pour gérer les prix et disponibilités
            </SelectCatalogText>
          </SelectCatalogMessage>
        )}
      </ScreenContainer>
    </MainLayout>
  );
};

// Styled components
const ScreenContainer = styled.View(({ theme }) => ({
  flex: 1,
  paddingTop: normalize(20),
  paddingRight: normalize(20),
  paddingBottom: normalize(20),
  paddingLeft: normalize(20) + theme.sizes.sidebarWidth,
}));

const LoadingContainer = styled.View(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.oxfordblue,
  borderRadius: normalize(12),
  justifyContent: 'center',
  alignItems: 'center',
  gap: normalize(16),
}));

const LoadingText = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.babypowder,
  opacity: 0.7,
}));

const HeaderSection = styled.View({
  marginBottom: normalize(24),
});

const HeaderTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(28),
  fontWeight: '700',
  color: theme.colors.prussianblue,
  marginBottom: normalize(4),
}));

const HeaderSubtitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.satanGray,
}));

const CatalogSelector = styled.View({
  marginBottom: normalize(20),
});

const SelectorLabel = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(13),
  fontWeight: '600',
  color: theme.colors.prussianblue,
  marginBottom: normalize(10),
  textTransform: 'uppercase',
  letterSpacing: 1,
}));

const CatalogButtonsRow = styled.View({
  flexDirection: 'row',
  gap: normalize(10),
  flexWrap: 'wrap',
});

const CatalogButton = styled.TouchableOpacity<{ selected: boolean }>(({ theme, props }) => ({
  backgroundColor: props.selected ? theme.colors.celadonblue : theme.colors.babypowder,
  borderRadius: normalize(8),
  paddingVertical: normalize(10),
  paddingHorizontal: normalize(16),
  borderWidth: 1,
  borderColor: props.selected ? theme.colors.celadonblue : theme.colors.gainsboro,
}));

const CatalogButtonText = styled.Text<{ selected: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '600',
  color: props.selected ? '#FFF' : theme.colors.prussianblue,
}));

const CatalogConnectorText = styled.Text<{ selected: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(11),
  color: props.selected ? 'rgba(255,255,255,0.7)' : theme.colors.satanGray,
  marginTop: normalize(2),
}));

const ContentContainer = styled.View(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.oxfordblue,
  borderRadius: normalize(12),
  padding: normalize(20),
  overflow: 'hidden',
}));

const FiltersRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: normalize(20),
  gap: normalize(16),
});

const SearchContainer = styled.View({
  position: 'relative',
  width: normalize(300),
});

const SearchInput = styled.TextInput(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: normalize(8),
  paddingVertical: normalize(10),
  paddingHorizontal: normalize(16),
  paddingRight: normalize(40),
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.babypowder,
}));

const ClearButton = styled.TouchableOpacity({
  position: 'absolute',
  right: normalize(12),
  top: '50%',
  transform: [{ translateY: -normalize(10) }],
  width: normalize(20),
  height: normalize(20),
  alignItems: 'center',
  justifyContent: 'center',
});

const ClearIcon = styled.Text(({ theme }) => ({
  color: theme.colors.babypowder,
  fontSize: normalize(18),
  fontWeight: 'bold',
}));

const TagFiltersContainer = styled.ScrollView({
  flex: 1,
});

const TagButton = styled.TouchableOpacity<{ selected: boolean }>(({ theme, props }) => ({
  backgroundColor: props.selected ? theme.colors.celadonblue : 'rgba(255,255,255,0.08)',
  borderRadius: normalize(20),
  paddingVertical: normalize(8),
  paddingHorizontal: normalize(16),
  marginRight: normalize(8),
}));

const TagButtonText = styled.Text<{ selected: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(13),
  fontWeight: props.selected ? '600' : '400',
  color: theme.colors.babypowder,
}));

const ListContainer = styled.View({
  flex: 1,
});

const ListHeader = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: normalize(16),
  paddingVertical: normalize(10),
  borderBottomWidth: 1,
  borderBottomColor: 'rgba(255,255,255,0.1)',
  marginBottom: normalize(12),
});

const HeaderCol = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(11),
  fontWeight: '600',
  color: 'rgba(255,255,255,0.5)',
  textTransform: 'uppercase',
  letterSpacing: 1,
}));

const SectionHeader = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: normalize(16),
  marginBottom: normalize(12),
  paddingHorizontal: normalize(8),
});

const SectionTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  fontWeight: '700',
  color: theme.colors.gold,
  letterSpacing: 2,
}));

const SectionCount = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  color: 'rgba(255,255,255,0.5)',
  marginLeft: normalize(8),
}));

const EmptySection = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: 'rgba(255,255,255,0.4)',
  textAlign: 'center',
  paddingVertical: normalize(20),
}));

const EmptyContainer = styled.View(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  padding: normalize(40),
  paddingLeft: normalize(40) + theme.sizes.sidebarWidth,
}));

const EmptyText = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  color: theme.colors.satanGray,
  textAlign: 'center',
}));

const SelectCatalogMessage = styled.View(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.oxfordblue,
  borderRadius: normalize(12),
  justifyContent: 'center',
  alignItems: 'center',
  padding: normalize(40),
}));

const SelectCatalogText = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  color: 'rgba(255,255,255,0.5)',
  textAlign: 'center',
}));
